/**
 * Workshop registry. Ported from the `mc-bottom-nav` block in
 * `_legacy/mc.html` (lines 1887-2120). Each workshop has a stable
 * id (`WS1`-`WS8`), a one-based number, a short navigator title,
 * a status (matches the vanilla `.ws-done` / `.ws-active` / `.ws-locked`
 * classes), and the DXC accent token used to colour its bubble panel
 * and step-bar highlight.
 *
 * Status semantics:
 *   - `done`        prior workshops, completed — true-blue with check icon
 *   - `active`      the in-flight workshop — sky, highlighted, glow
 *   - `locked`      upcoming — sky (still accessible in the demo, vanilla
 *                   distinguished `ws-locked` (sky/peach) from `ws-locked-gray`
 *                   (truly inaccessible). We're treating all locked as
 *                   browseable for now.
 */

import type { ActivePanel } from "@/stores/useNavigation";

export type WorkshopStatus = "done" | "active" | "locked";

export type Workshop = {
  id: "WS1" | "WS2" | "WS3" | "WS4" | "WS5" | "WS6" | "WS7" | "WS8";
  num: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  title: string;
  /** Longer label used in the panel header. */
  longTitle: string;
  status: WorkshopStatus;
  /**
   * DXC accent token. Drives the bubble stroke + step-bar highlight.
   * The token resolves to the right hue per theme via src/index.css.
   */
  accent:
    | "true-blue"
    | "sky"
    | "gold"
    | "melon"
    | "peach";
  /** Matching ActivePanel id for the panel switcher. */
  panel: ActivePanel;
};

export const WORKSHOPS: Workshop[] = [
  {
    id: "WS1",
    num: 1,
    title: "Executive\nAlignment",
    longTitle: "WS1 – Executive Alignment",
    status: "done",
    accent: "true-blue",
    panel: "MC_WS1",
  },
  {
    id: "WS2",
    num: 2,
    title: "Process\nSelection",
    longTitle: "WS2 – Current Process Selection & Baseline",
    status: "done",
    accent: "true-blue",
    panel: "MC_WS2",
  },
  {
    id: "WS3",
    num: 3,
    title: "AI Process\nDesign",
    longTitle: "WS3 – To Be AI Enhanced Process Design",
    status: "done",
    accent: "sky",
    panel: "MC_WS3",
  },
  {
    id: "WS4",
    num: 4,
    title: "Ways of\nWorking",
    longTitle:
      "WS4 – Human-Agent Ways of Working and Operating Model",
    status: "active",
    accent: "sky",
    panel: "MC_WS4",
  },
  {
    id: "WS5",
    num: 5,
    title: "Governance\n& Risk",
    longTitle: "WS5 – Governance, Risk Compliance & Safety",
    status: "locked",
    accent: "gold",
    panel: "MC_WS5",
  },
  {
    id: "WS6",
    num: 6,
    title: "Blueprint\nDemo",
    longTitle: "WS6 – Phase 0 Blueprint Demo",
    status: "locked",
    accent: "gold",
    panel: "MC_WS6",
  },
  {
    id: "WS7",
    num: 7,
    title: "Executive\nShowcase",
    longTitle: "WS7 – Executive Showcase",
    status: "locked",
    accent: "gold",
    panel: "MC_WS7",
  },
  {
    id: "WS8",
    num: 8,
    title: "Flight\nPlan",
    longTitle: "PREPARE – Flight Plan",
    status: "locked",
    accent: "melon",
    panel: "MC_WS8",
  },
];
